import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Flag from 'react-flagkit';
import { getCountries } from '../../../../utils/country.utils';
import usePhoneValidation from './hooks/usePhoneValidation';
import { useAuth } from '../../../../context/AuthContext';

// Custom hook for debouncing values
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Screen reader only styles
const srOnlyStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: '0'
};

const UnifiedPhoneInput = React.memo(({
  // Essential props
  name,
  value = '',
  phonePrefix,
  onChange,

  // Customization props
  placeholder = 'Téléphone',
  disabled = false,
  required = false,

  // Validation props
  error,
  touched = false,

  // Style props
  className = '',
  size = 'md',

  // Advanced props
  defaultCountry = 'FR',
  onCountryChange
}) => {
  const countries = useMemo(() => getCountries(), []);
  const { validatePhone } = usePhoneValidation(required);
  const { user } = useAuth();

  // Component state management
  const [selectedCountry, setSelectedCountry] = useState(() => {
    // 1. Si un phonePrefix est fourni explicitement, l'utiliser
    if (phonePrefix) {
      return countries.find(country => country.phonePrefix === phonePrefix) ||
        countries.find(country => country.iso === defaultCountry) ||
        countries[0];
    }

    // 2. Si pas de phonePrefix mais utilisateur connecté avec un préfixe, l'utiliser
    if (user?.phonePrefix) {
      return countries.find(country => country.phonePrefix === user.phonePrefix) ||
        countries.find(country => country.iso === defaultCountry) ||
        countries[0];
    }

    // 3. Sinon, utiliser le pays par défaut (France)
    return countries.find(country => country.iso === defaultCountry) || countries[0];
  }, []);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [internalError, setInternalError] = useState(null);
  const [hasBeenTouched, setHasBeenTouched] = useState(touched);

  // Debounce search term to improve dropdown performance
  const debouncedSearchTerm = useDebounce(searchTerm, 150);

  // Refs for keyboard navigation
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const countryButtonRef = useRef(null);
  const liveRegionRef = useRef(null);

  // Initialize phonePrefix if not provided
  useEffect(() => {
    // Si aucun phonePrefix n'est fourni, initialiser avec le préfixe de l'utilisateur ou le défaut
    if (!phonePrefix && selectedCountry) {
      onChange({ target: { name: 'phonePrefix', value: selectedCountry.phonePrefix } });
    }
  }, [phonePrefix, selectedCountry, onChange]);

  // Update selected country when phonePrefix prop changes
  useEffect(() => {
    if (phonePrefix) {
      const country = countries.find(country => country.phonePrefix === phonePrefix);
      if (country && country.iso !== selectedCountry.iso) {
        setSelectedCountry(country);
      }
    }
  }, [phonePrefix, countries, selectedCountry.iso]);

  // Update internal touched state when prop changes
  useEffect(() => {
    setHasBeenTouched(touched);
  }, [touched]);

  // Clear internal error when user starts correcting input
  useEffect(() => {
    if (value && internalError) {
      const validationError = validatePhone(value, selectedCountry);
      if (!validationError) {
        setInternalError(null);
      } else {
        setInternalError(validationError);
      }
    }
  }, [value, selectedCountry, validatePhone, internalError]);

  // Handle input changes (manual country selection only)
  const handleInputChange = useCallback((event) => {
    let inputValue = event.target.value;

    // Mark as touched when user starts typing
    if (!hasBeenTouched) {
      setHasBeenTouched(true);
    }

    // Clean the number (remove leading spaces and any + prefix)
    inputValue = inputValue.trimStart().replace(/^\+/, '');

    // Validate the input and clear errors if valid
    const validationError = validatePhone(inputValue, selectedCountry);
    if (validationError && hasBeenTouched) {
      setInternalError(validationError);
    } else if (!validationError) {
      setInternalError(null);
    }

    // Update phone number value
    onChange({ target: { name, value: inputValue } });
  }, [hasBeenTouched, onChange, name, validatePhone, selectedCountry]);

  // Handle manual country selection
  const handleCountryChange = useCallback((iso) => {
    const country = countries.find(country => country.iso === iso);
    setSelectedCountry(country);
    onChange({ target: { name: 'phonePrefix', value: country.phonePrefix } });

    // Notify country change if callback provided
    if (onCountryChange) {
      onCountryChange(country);
    }

    // Re-validate with new country if field has been touched
    if (hasBeenTouched && value) {
      const validationError = validatePhone(value, country);
      setInternalError(validationError);
    }

    setDropdownOpen(false);
    setSearchTerm('');
  }, [countries, onChange, onCountryChange, hasBeenTouched, value, validatePhone]);

  // Handle input blur for validation
  const handleInputBlur = useCallback(() => {
    setHasBeenTouched(true);

    // Validate on blur
    const validationError = validatePhone(value, selectedCountry);
    setInternalError(validationError);
  }, [value, selectedCountry, validatePhone]);

  // Filter countries based on debounced search term to avoid recalculation
  const filteredCountries = useMemo(() =>
    countries.filter(country =>
      country.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    ), [countries, debouncedSearchTerm]
  );

  // Reset highlighted index when filtered countries change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredCountries]);

  // Handle keyboard navigation in dropdown
  const handleDropdownKeyDown = useCallback((e) => {
    if (!dropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (filteredCountries.length > 0) {
          setHighlightedIndex(prev =>
            prev < filteredCountries.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (filteredCountries.length > 0) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredCountries.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          handleCountryChange(filteredCountries[highlightedIndex].iso);
          // Return focus to the country button after selection
          setTimeout(() => countryButtonRef.current?.focus(), 100);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setDropdownOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        // Return focus to the country button
        countryButtonRef.current?.focus();
        break;
      case 'Tab':
        // Allow natural tab behavior to close dropdown
        setDropdownOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      case 'Home':
        e.preventDefault();
        if (filteredCountries.length > 0) {
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        e.preventDefault();
        if (filteredCountries.length > 0) {
          setHighlightedIndex(filteredCountries.length - 1);
        }
        break;
    }
  }, [dropdownOpen, filteredCountries.length, highlightedIndex, handleCountryChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  // Focus management and trap for dropdown
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      // Focus the search input when dropdown opens
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  // Scroll highlighted option into view and announce to screen readers
  useEffect(() => {
    if (dropdownOpen && highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
      const listbox = document.getElementById(`${name}-listbox`);
      const options = listbox?.querySelectorAll('[role="option"]');
      const highlightedOption = options?.[highlightedIndex];

      if (highlightedOption) {
        highlightedOption.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }

      // Announce the highlighted country to screen readers
      const country = filteredCountries[highlightedIndex];
      if (liveRegionRef.current && country) {
        liveRegionRef.current.textContent = `${country.name}, ${country.phonePrefix}`;
      }
    }
  }, [highlightedIndex, dropdownOpen, name, filteredCountries]);

  // Memoize computed values to prevent unnecessary recalculations
  const displayCountry = useMemo(() =>
    phonePrefix
      ? (countries.find(country => country.phonePrefix === phonePrefix) || selectedCountry)
      : selectedCountry,
    [phonePrefix, countries, selectedCountry]
  );

  const displayError = useMemo(() => error || internalError, [error, internalError]);
  const shouldShowError = useMemo(() => (touched || hasBeenTouched) && displayError, [touched, hasBeenTouched, displayError]);

  // Size classes mapping
  const sizeClasses = useMemo(() => ({
    sm: 'input-sm text-sm',
    md: 'input-md text-base',
    lg: 'input-lg text-lg'
  }), []);

  const inputSizeClass = sizeClasses[size] || sizeClasses.md;

  // Memoized event handlers for country button
  const handleCountryButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDropdownOpen(!dropdownOpen);
      if (!dropdownOpen) {
        // Focus search input when opening dropdown
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [disabled, dropdownOpen]);

  const handleCountryButtonKeyDown = useCallback((e) => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setDropdownOpen(!dropdownOpen);
      if (!dropdownOpen) {
        // Focus search input when opening dropdown
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    } else if (!disabled) {
      handleDropdownKeyDown(e);
    }
  }, [disabled, dropdownOpen, handleDropdownKeyDown]);

  // Memoized search input change handler
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Memoized phone input key down handler
  const handlePhoneInputKeyDown = useCallback((e) => {
    // Allow normal input behavior, but handle special cases
    if (e.key === 'Tab') {
      // Close dropdown if open when tabbing away
      if (dropdownOpen) {
        setDropdownOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    }
  }, [dropdownOpen]);

  return (
    <div className={`unified-phone-input ${className}`} role="group" aria-labelledby={`${name}-label`}>
      <div
        className={`input input min-h-[44px] ${inputSizeClass}  ${shouldShowError ? 'input-error' : 'input-primary'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="dropdown w-fit" ref={dropdownRef}>
          <div
            ref={countryButtonRef}
            tabIndex={disabled ? -1 : 0}
            role="combobox"
            className={`flex items-center gap-3 py-2 rounded-lg transition-all duration-200 ${disabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer'
              }`}
            style={{
              pointerEvents: 'auto',
              zIndex: 10,
              position: 'relative',
              minWidth: '80px',

            }}
            onClick={handleCountryButtonClick}
            onKeyDown={handleCountryButtonKeyDown}
            aria-expanded={dropdownOpen}
            aria-haspopup="listbox"
            aria-controls={dropdownOpen ? `${name}-listbox` : undefined}
            aria-activedescendant={dropdownOpen && highlightedIndex >= 0 ? `${name}-option-${highlightedIndex}` : undefined}
            aria-label={`Sélectionner le pays, actuellement ${displayCountry.name} ${displayCountry.phonePrefix}`}
            aria-describedby={shouldShowError ? `${name}-error` : `${name}-help`}
          >
            <Flag
              country={displayCountry.iso}
              size={24}
              className="rounded-sm shadow-sm"
            />
            <span className={`text-base font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
              {displayCountry.phonePrefix}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''} ${disabled ? 'text-gray-400' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {dropdownOpen && !disabled && (
            <div
              id={`${name}-listbox`}
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden"
              role="listbox"
              aria-label="Liste des pays disponibles"
              style={{
                maxHeight: '320px',
                minWidth: '300px'
              }}
            >
              <div className="p-3 border-b border-gray-100">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Rechercher un pays..."
                  className="w-full px-4 py-1 text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: 'none'
                  }}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleDropdownKeyDown}
                  aria-label="Rechercher un pays"
                  aria-describedby={`${name}-search-help`}
                  aria-controls={`${name}-listbox`}
                  aria-activedescendant={highlightedIndex >= 0 ? `${name}-option-${highlightedIndex}` : undefined}
                  role="searchbox"
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>
              <div id={`${name}-search-help`} style={srOnlyStyles}>
                Tapez pour filtrer la liste des pays. Utilisez les flèches haut/bas pour naviguer, Entrée pour sélectionner, Échap pour fermer.
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '260px' }}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country, index) => {
                    const isSelected = country.iso === displayCountry.iso;
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <div
                        key={`${country.iso}-${country.phonePrefix}`}
                        onClick={() => handleCountryChange(country.iso)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseLeave={() => setHighlightedIndex(-1)}
                        role="option"
                        aria-selected={isSelected}
                        aria-label={`${country.name}, préfixe ${country.phonePrefix}`}
                        tabIndex={-1}
                        id={`${name}-option-${index}`}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 ${isHighlighted ? 'bg-blue-50' : ''
                          } ${isSelected ? 'bg-primary/20 border-l-4 border-primary' : ''} hover:bg-gray-50`}
                      >
                        <Flag country={country.iso} size={24} className="rounded-sm shadow-sm" aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <span className="text-base font-medium text-gray-900 block truncate">{country.name}</span>
                        </div>
                        <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {country.phonePrefix}
                        </span>
                        {isSelected && (
                          <>
                            <svg className="w-5 h-5 text-base-content" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span style={srOnlyStyles}>Sélectionné</span>
                          </>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-sm">Aucun pays trouvé</p>
                    <p className="text-xs text-gray-400 mt-1">Essayez un autre terme de recherche</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <label htmlFor={name} style={{ display: 'none' }} id={`${name}-label`}>
          Numéro de téléphone
        </label>
        <input
          id={name}
          name={name}
          required={required}
          type="tel"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          value={value || ''}
          disabled={disabled}
          className={`grow w-full h-full p-2 focus:outline-none border-l border-solid border-gray-300 border-y-0 border-r-0 mr-3 pl-2 ${disabled ? 'cursor-not-allowed bg-transparent' : ''}`}
          placeholder={placeholder}
          aria-invalid={shouldShowError}
          aria-describedby={shouldShowError ? `${name}-error ${name}-help` : `${name}-help`}
          aria-label={`Numéro de téléphone pour ${displayCountry.name}`}
          autoComplete="tel"
          onKeyDown={handlePhoneInputKeyDown}
        />
      </div>

      <div id={`${name}-help`} style={srOnlyStyles}>
        Champ de saisie de numéro de téléphone. Le pays sélectionné est {displayCountry.name} avec le préfixe {displayCountry.phonePrefix}.
        {!disabled && "Cliquez sur le drapeau ou utilisez Tab puis Entrée pour changer de pays."}
      </div>

      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        style={srOnlyStyles}
      />

      {shouldShowError && (
        <div
          id={`${name}-error`}
          className="error-message text-red-500 text-sm mt-1 transition-all duration-200"
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          {displayError}
        </div>
      )}
    </div>
  );
});

// PropTypes for TypeScript-style validation
UnifiedPhoneInput.propTypes = {
  // Essential props
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  phonePrefix: PropTypes.string,
  onChange: PropTypes.func.isRequired,

  // Customization props
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,

  // Validation props
  error: PropTypes.string,
  touched: PropTypes.bool,

  // Style props
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),

  // Advanced props
  defaultCountry: PropTypes.string,
  onCountryChange: PropTypes.func
};

export default UnifiedPhoneInput;